import { getWebPath } from '@doss/common-all'
import dayjs from 'dayjs'
import { isNil } from 'lodash'
import { MenuIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { RxArrowLeft } from 'react-icons/rx'
import { useNavigate, useSearchParams } from 'react-router'
import { EditableTextField } from '~/@/components/editable-text-field'
import {
  BreadcrumbObjectType,
  useGetWorkflowNameQuery,
  useGetWorkspaceByObjectQuery,
} from '~/common/generated/apollo-operations'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import { useBreadcrumbs } from '~/common/hooks/useBreadcrumbs'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useEmbedTitle } from '~/common/hooks/useEmbedTitle'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { usePage } from '~/common/hooks/usePage'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { ObjectsWithoutWorkspace } from '~/common/utils/breadcrumbs'
import { formatDisplayTimestamp } from '~/common/utils/display-timestamp'
import { AnimatedDossbotIcon } from '~/components/dossbot/AnimatedDossbotIcon'
import { Chip } from '~/components/ui/atoms/chip/Chip'
import useBackLink from '../../common/hooks/useBackLink'
import { DossIcon } from '../DossIcon'
import { FormsSearchInput } from '../forms-home/FormsSearchInput'
import { WorkspaceActionButtons } from '../renderers/workspaces/WorkspaceActionButtons'
import Link from '../shared/Link'
import { TitleBarProps } from './TitleBar'
import { TitleBarExpandIcon } from './TitleBarExpandIcon'
import { TitleBarSkeleton } from './TitleBarSkeleton'
import { WorkspaceIcon } from './WorkspaceIcon'

export function TitleBarHeader({
  showBackLink,
  isLeftNavExpanded,
  isLeftNavPeeking,
  sharedObject,
  isUnauthenticatedSharedRoute,
  chart,
  onLeftNavPeekStart,
  onLeftNavPeekEnd,
}: TitleBarProps) {
  const { isMobile } = useDeviceType()
  const { t } = useTypedTranslation()
  const backLinkData = useBackLink()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    dispatch,
    subscribe,
    unsubscribe,
    showPivotTableDrilldown,
    setPivotTableDrilldownChartName,
    pivotTableDrilldownChartName,
  } = useDossSignals()
  const { permissionsManager } = useGlobalContext()
  const { breadcrumbs, loading: breadcrumbsLoading } = useBreadcrumbs()
  const { objectId, objectType, pageType, crumbs, group, query } = usePage()
  const leafCrumb = crumbs.at(-1)
  const workflowIdParam = typeof query.workflow_id === 'string' ? query.workflow_id : undefined
  const { data: workflowNameData } = useGetWorkflowNameQuery({
    variables: { workflowId: workflowIdParam ?? '' },
    skip: !workflowIdParam,
  })
  const selectedActionId = searchParams.get('action') ?? undefined
  const selectedActionName =
    selectedActionId && workflowNameData?.workflow.actions
      ? workflowNameData.workflow.actions.find((a) => a.id === selectedActionId)?.name
      : undefined

  const { data: workspaceByObject } = useGetWorkspaceByObjectQuery({
    variables: {
      input: {
        objectId: objectId || '',
        objectType: objectType ?? BreadcrumbObjectType.ORGANIZATION,
      },
    },
    skip:
      isUnauthenticatedSharedRoute ||
      !objectId ||
      !objectType ||
      ObjectsWithoutWorkspace.has(objectType) ||
      !isNil(sharedObject),
  })

  // Embed pages surface when the page last brought data in: the guest runs its queries on
  // mount, so the mount timestamp is the data's "as of" time. A full page refresh re-captures it.
  const isEmbedPage = objectType === BreadcrumbObjectType.EMBED
  // Under configbase navigation an app page is titled by its nav label and route, not a project
  const embedTitle = useEmbedTitle()
  const [embedDataRefreshedAt] = useState(() => dayjs().toISOString())
  const [invalidPivotTableName, setInvalidPivotTableName] = useState(false)

  useEffect(() => {
    const invalidPivotTableNameId = subscribe(DossSignalType.INVALID_PIVOT_TABLE_NAME, async () => {
      setInvalidPivotTableName(true)
    })
    const validPivotTableNameId = subscribe(DossSignalType.VALID_PIVOT_TABLE_NAME, async () => {
      setInvalidPivotTableName(false)
    })

    return () => {
      unsubscribe(invalidPivotTableNameId)
      unsubscribe(validPivotTableNameId)
    }
  }, [subscribe, unsubscribe])

  const icon = backLinkData.icon
  const [objectName, workspaceType, objectIcon] = useMemo(() => {
    if (pageType === 'pivotTable') {
      const pivotTableName = breadcrumbs[breadcrumbs.length - 1]?.name || t('title_bar.untitled')

      // If not showing drilldown, show the editable text field
      if (!showPivotTableDrilldown) {
        return [
          <div key="edit-pivot-table-name-container" className="relative">
            <EditableTextField
              key="pivot-table-name"
              className="-ms-1.5"
              defaultValue={pivotTableName}
              onBlur={(e) => {
                dispatch(DossSignalType.CHART_NAME_SAVED, { name: e.target.value })
                setPivotTableDrilldownChartName(e.target.value)
              }}
              onSave={(name = '') => {
                dispatch(DossSignalType.CHART_NAME_SAVED, { name })
                setPivotTableDrilldownChartName(name)
              }}
            />
            {invalidPivotTableName && (
              <div className="absolute -bottom-3 start-0 text-xs text-red-9">
                {t('pivot_table.rename.duplicate_name')}
              </div>
            )}
          </div>,
          BreadcrumbObjectType.PIVOT_TABLE,
          undefined,
        ]
      } else {
        // For drilldown view, show "PivotTableName / PivotTableName Drilldown"
        const drilldownText = `${t('pivot_table.drilldown')}`
        return [
          <div key="pivot-table-name-container" className="flex items-center gap-2">
            <div
              className="cursor-pointer text-gray-8"
              onClick={() => dispatch(DossSignalType.RESET_PIVOT_TABLE_DRILLDOWN)}
            >
              {pivotTableDrilldownChartName}
            </div>
            <div className="text-gray-8">/</div>
            <div>
              {pivotTableDrilldownChartName} {drilldownText}
            </div>
          </div>,
          BreadcrumbObjectType.PIVOT_TABLE,
          undefined,
        ]
      }
    }
    if (pageType === 'createPivotTable' || pageType === 'pivotTableSettings') {
      // If the an unauthorized user somehow navigates to this page, they should not be able to edit the name
      if (chart?.table.id && !permissionsManager.canConfigurePivotTables(chart.table.id)) {
        return [chart.name, BreadcrumbObjectType.PIVOT_TABLE, undefined]
      }

      // If not showing drilldown, show the editable text field
      if (!showPivotTableDrilldown) {
        return [
          <div key="create-pivot-table-name-container" className="relative">
            <EditableTextField
              key="create-pivot-table-name"
              className="-ms-1.5"
              defaultValue={pivotTableDrilldownChartName}
              onBlur={(e) => {
                dispatch(DossSignalType.CHART_NAME_SAVED, { name: e.target.value })
                setPivotTableDrilldownChartName(e.target.value)
              }}
              onSave={(name = '') => {
                dispatch(DossSignalType.CHART_NAME_SAVED, { name })
                setPivotTableDrilldownChartName(name)
              }}
            />
            {invalidPivotTableName && (
              <div className="absolute -bottom-3 start-0 text-xs text-red-9">
                {t('pivot_table.rename.duplicate_name')}
              </div>
            )}
          </div>,
          BreadcrumbObjectType.PIVOT_TABLE,
          undefined,
        ]
      } else {
        // For drilldown view, show "PivotTableName / PivotTableName Drilldown"
        const drilldownText = `${t('pivot_table.drilldown')}`
        return [
          <div key="pivot-table-name-container" className="flex items-center gap-2">
            <div
              className="cursor-pointer text-gray-8"
              onClick={() => dispatch(DossSignalType.RESET_PIVOT_TABLE_DRILLDOWN)}
            >
              {pivotTableDrilldownChartName}
            </div>
            <div className="text-gray-8">/</div>
            <div>
              {pivotTableDrilldownChartName} {drilldownText}
            </div>
          </div>,
          BreadcrumbObjectType.PIVOT_TABLE,
          undefined,
        ]
      }
    }
    if (objectType === BreadcrumbObjectType.CHART || pageType === 'createChart') {
      const canEditExistingChart = chart ? permissionsManager.canConfigureChart(chart) : false
      if (canEditExistingChart || pageType === 'createChart') {
        return [
          <EditableTextField
            key={0}
            className="-ms-1.5"
            defaultValue={
              pageType === 'createChart'
                ? t('title_bar.untitled')
                : breadcrumbs[breadcrumbs.length - 1]?.name || t('title_bar.untitled')
            }
            onBlur={(e) => {
              dispatch(DossSignalType.CHART_NAME_SAVED, { name: e.target.value })
            }}
            onSave={(name = '') => {
              dispatch(DossSignalType.CHART_NAME_SAVED, { name })
            }}
          />,
          BreadcrumbObjectType.CHART,
          undefined,
        ]
      }
      return [
        breadcrumbs[breadcrumbs.length - 1]?.name || t('title_bar.untitled'),
        BreadcrumbObjectType.CHART,
        undefined,
      ]
    }
    if (pageType === 'workspace' || pageType === 'createWorkspace') {
      const canEditWorkspace = objectId ? permissionsManager.canEditWorkspace(objectId) : false
      if (canEditWorkspace) {
        return [
          <EditableTextField
            key={0}
            className="-ms-1.5"
            defaultValue={breadcrumbs[breadcrumbs.length - 1]?.name}
            onBlur={(e) => {
              dispatch(DossSignalType.WORKSPACE_NAME_SAVED, {
                workspaceId: objectId,
                name: e.target.value,
              })
            }}
            onSave={(name = '') => {
              dispatch(DossSignalType.WORKSPACE_NAME_SAVED, { workspaceId: objectId, name })
            }}
          />,
          BreadcrumbObjectType.WORKSPACE,
          undefined,
        ]
      }
      return [breadcrumbs[breadcrumbs.length - 1]?.name, BreadcrumbObjectType.WORKSPACE]
    }
    if (pageType === 'workflow') {
      const composed = selectedActionName ? (
        <span key="workflow-title" className="flex items-center gap-2">
          <span>{backLinkData.name}</span>
          <span className="text-gray-8">/</span>
          <span>{selectedActionName}</span>
        </span>
      ) : (
        backLinkData.name
      )
      return [composed, undefined, undefined]
    }
    if (pageType === 'dossbot') {
      return [
        t('title_bar.dossbot'),
        BreadcrumbObjectType.USER,
        <AnimatedDossbotIcon key="dossbot-icon" animate={false} height={20} width={20} />,
      ]
    }
    if (sharedObject) {
      return [sharedObject.objectName, sharedObject.workspaceType, undefined]
    }
    // Static label pages: use the leaf crumb from the route hierarchy
    // Covers: settings, formsHome, workflowHome, createForm, tableSettings, tableExports,
    // chartSettings, pivotTableSettings, apollo, dependencyExplorer
    if (leafCrumb) {
      let fallbackType: BreadcrumbObjectType = BreadcrumbObjectType.USER
      if (group === 'workflows') {
        fallbackType = BreadcrumbObjectType.WORKFLOW
      } else if (group === 'forms') {
        fallbackType = BreadcrumbObjectType.FORM
      }
      return [t(leafCrumb.label as TranslationKeys), objectType ?? fallbackType, undefined]
    }

    return [
      breadcrumbs[breadcrumbs.length - 1]?.name,
      breadcrumbs[breadcrumbs.length - 1]?.workspaceType,
    ]
  }, [
    pageType,
    objectType,
    showPivotTableDrilldown,
    sharedObject,
    breadcrumbs,
    chart,
    permissionsManager,
    dispatch,
    objectId,
    backLinkData.name,
    selectedActionName,
    group,
    leafCrumb,
    pivotTableDrilldownChartName,
    setPivotTableDrilldownChartName,
    invalidPivotTableName,
    t,
  ])
  const iconForDisplay =
    icon && pageType === 'table' ? <DossIcon name={icon.name} size={16} /> : objectIcon
  const iconForBackLink = icon ? <DossIcon name={icon.name} size={16} /> : undefined

  // For create chart, create pivot table, and pivot table settings, display a back button to
  // go back to the previous route
  const showBackButton =
    pageType === 'createChart' ||
    pageType === 'createPivotTable' ||
    pageType === 'pivotTableSettings' ||
    pageType === 'chartSettings'

  const workspaceId = searchParams.get('workspaceId') ?? undefined
  const tableId = searchParams.get('tableId') ?? undefined

  // Example with fallback
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else if (workspaceId) {
      navigate(getWebPath({ objectType: BreadcrumbObjectType.WORKSPACE, objectId: workspaceId }))
    } else if (tableId) {
      navigate(getWebPath({ objectType: BreadcrumbObjectType.TABLE, objectId: tableId }))
    }
  }

  if (breadcrumbsLoading) {
    return <TitleBarSkeleton />
  }

  return (
    <>
      <div className="flex flex-row items-center gap-2">
        <div className="flex h-[52px] items-center">
          {isMobile && (
            <div
              className="me-3 cursor-pointer p-1.5"
              onClick={() => dispatch(DossSignalType.TOGGLE_MOBILE_MENU)}
            >
              <MenuIcon className="size-5" />
            </div>
          )}
          {showBackLink && embedTitle === null && (
            <Link
              className="flex items-center gap-4 pe-2"
              href={getWebPath(backLinkData)}
              noLinkStyle
            >
              <div className="flex items-center gap-2 text-sm text-gray-9">
                <div className="flex items-center gap-2 hover:text-gray-11">
                  {!isMobile && (
                    <TitleBarExpandIcon
                      icon={iconForBackLink}
                      isLeftNavExpanded={isLeftNavExpanded}
                      objectType={backLinkData.objectType}
                      onPeekEnd={onLeftNavPeekEnd}
                      onPeekStart={onLeftNavPeekStart}
                    />
                  )}
                  <div className="whitespace-nowrap">
                    {objectId && objectType === BreadcrumbObjectType.WORKFLOW
                      ? 'Workflows'
                      : backLinkData.name}
                  </div>
                </div>
                <div>/</div>
              </div>
            </Link>
          )}
          {showBackButton && !showBackLink && (
            <div className="p-4">
              <div className="cursor-pointer hover:text-gray-8" onClick={handleGoBack}>
                <RxArrowLeft />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-11">
            {!isMobile &&
              (!showBackLink ? (
                <TitleBarExpandIcon
                  icon={iconForDisplay}
                  isLeftNavExpanded={isLeftNavExpanded}
                  isPeeking={isLeftNavPeeking}
                  objectType={workspaceType}
                  onPeekEnd={onLeftNavPeekEnd}
                  onPeekStart={onLeftNavPeekStart}
                />
              ) : (
                <WorkspaceIcon
                  className="shrink-0"
                  icon={iconForDisplay}
                  size={16}
                  workspaceType={workspaceType}
                />
              ))}
            <div className="whitespace-nowrap">{embedTitle ?? objectName}</div>
            {isEmbedPage && (
              <span className="whitespace-nowrap text-xs italic text-gray-9">
                {t('embed.lastUpdatedAt')} {formatDisplayTimestamp(embedDataRefreshedAt)}
              </span>
            )}
            {!isNil(sharedObject) && (
              <Chip color="green" isRounded label="Shared View" variant="solid" />
            )}
          </div>
        </div>
        {pageType === 'formsHome' && (
          <div className="ms-4">
            <FormsSearchInput />
          </div>
        )}
      </div>
      {isNil(sharedObject) &&
        !leafCrumb &&
        pageType !== 'workflow' &&
        pageType !== 'createTable' &&
        pageType !== 'createForm' && (
          <WorkspaceActionButtons
            actions={
              workspaceByObject ? [...workspaceByObject.workspaceByObject.actionButtons] : []
            }
          />
        )}
    </>
  )
}
